<template>
    <div class="trading-pin-details row">
        <div class="col">

            <div class="row">
                <div class="col-12 col-sm mb-4 text-center">
                    <picture>
                        <source :srcset="'https://pinandpop.s3.amazonaws.com/images/pinails/' + thumb + '.webp'" type="image/webp">
                        <source :srcset="'https://pinandpop.s3.amazonaws.com/images/pinails/' + thumb + '.jpg'" type="image/jpeg">
                        <img class="img-thumbnail me-3" :src="'https://pinandpop.s3.amazonaws.com/images/pinails/' + thumb + '.jpg'" :alt="name">
                    </picture>
                    <h5 v-if="isOwned" class="mt-2 mb-0">
                        <span class="badge bg-own"><i class="fa-regular fa-circle-check"></i> You already OWN</span>
                    </h5>
                    <h5 v-if="isIso || isGrail" class="mt-2 mb-0">
                        <span v-if="isGrail" class="badge bg-grail"><i class="fa-regular fa-crown"></i> Grail Alert</span>
                        <span v-else class="badge bg-iso"><i class="fa-regular fa-magnifying-glass-plus"></i> ISO Alert</span>
                    </h5>
                </div>
                <div class="col">
                    <h5 class="mt-0" :class="{'mb-0': rarity}">{{ name }}</h5>
                    <h5 v-if="rarity" class="mb-1 rarity-container"><span class="rarity badge" :class="rarity">{{ rarity }}</span></h5>
                    <div class="mb-1"><span class="text-muted">P&amp;P-ID:</span> <a :href="url" target="_blank">{{ id }}</a></div>
                    <div class="mb-1"><span class="text-muted">Series:</span> <a v-if="series" :href="seriesUrl" target="_blank">{{ series }}</a></div>
                    <div class="mb-1"><span class="text-muted">Edition:</span> {{ edition }} 
                        <span v-if="editionCode === 'LE' && editionSize > 0">{{ editionSize }}</span>
                        <span v-else-if="editionCode === 'LE' && editionSize < 0" class="text-muted">(Undisclosed #)</span>
                    </div>
                    <div class="mb-1"><span class="text-muted">Released:</span> 
                        <span v-if="released"> {{ released }}</span>
                        <span v-else> Unknown</span>
                    </div>
                    <div class="mb-3"><span class="text-muted">Original Price:</span>
                        <span v-if="price"> {{ price }}</span>
                        <span v-else> Unknown</span>
                    </div>

                    <!-- <div v-if="condition || (ledger && ledger[3])" class="border rounded p-2"> -->
                    <div class="border rounded p-2">

                        <strong v-if="ledger && ledger[4]" class="fs-5 mb-2">Artist Proof (AP)</strong>
                        <strong v-else-if="ledger && ledger[5]" class="fs-5 mb-2">Pre-Production (PP)</strong>

                        <div>
                            <div><span class="text-muted me-2">Condition:</span> 
                                <strong v-if="condition === 'good'" class="px-2 bg-success text-light rounded">GOOD</strong>
                                <strong v-else-if="condition === 'flawed'" class="px-2 bg-secondary text-light rounded">Flawed</strong>
                                <strong v-else-if="condition === 'damaged'" class="px-2 bg-secondary text-light rounded">Damaged</strong>
                                <strong v-else-if="condition === 'fake'" class="px-2 bg-secondary text-light rounded">Fake</strong>
                                <span v-else>Not specified</span>
                            </div>
                            <div>
                                <span class="text-muted">Condition Notes:</span>
                                <div v-if="ledger && ledger[2]" class="ms-2 fw-bold">{{ ledger[2].value }}</div>
                                <span v-else class="ms-2">Not specified</span>
                            </div>
                        </div>

                        <!-- <div v-if="ledger && ledger[3]" class="mt-2"> -->
                        <div class="mt-2">
                            <span class="text-muted me-2">Backer Card/Box:</span>
                            <strong v-if="ledger && ledger[3]">{{ ledger[3].value === '1' ? 'Yes' : 'No' }}</strong>
                            <span v-else>Not specified</span>
                        </div>
                    </div>

                    <div class="mt-3 alert alert-grail">
                        <h5 class="alert-heading">Trader's Interests:</h5>
                        <div v-if="tags.length">
                            {{ tags.map(t => t.name).join(', ') }}
                        </div>
                        <div v-else>
                            <span>Unknown</span>
                        </div>
                        <h6 v-if="summary" class="mt-2 mb-0 fw-bold">Trader's Note:</h6>
                        <span class="fst-italic">{{ summary }}</span>
                        <div class="mt-2 d-flex justify-content-between">
                            <a v-if="table && table.url" :href="table.url">Trader's Table</a>
                            <a v-if="profileUrl" :href="profileUrl" target="_blank">Trader's Profile</a>
                        </div>
                    </div>

                    <div class="mt-3 alert alert-grail">
                        <div class="d-flex justify-content-between">
                            <div class="">Accepts Trades</div>
                            <div class="d-flex gap-1 align-items-center fw-bold">
                                <template v-for="n in Math.floor(ratings.canceled_rating)">
                                <i class="fa-solid fa-star"></i>
                                </template>
                                <template v-for="n in (5 - Math.floor(ratings.canceled_rating))">
                                <i v-if="n === 1 && (ratings.canceled_rating - Math.floor(ratings.canceled_rating) < 1)" class="fa-solid fa-star-half-stroke"></i>
                                <i v-else class="fa-light fa-star"></i>
                                </template>
                            </div>
                        </div>
                        <div class="small text-end">
                            <p class="text-muted">
                                <span v-if="ratings.canceled_rating < 2.5">Frequently</span>
                                <span v-else-if="ratings.canceled_rating < 3.5">Sometimes</span>
                                <span v-else-if="ratings.canceled_rating < 5">Rarely</span>
                                <span v-else>Never</span>
                                cancels trades of verified pins
                            </p>
                        </div>

                        <div class="d-flex justify-content-between mt-4">
                            <div class="">Pins-in-Hand</div>
                            <div class="d-flex gap-1 align-items-center fw-bold">
                                <template v-for="n in Math.floor(ratings.missing_rating)">
                                <i class="fa-solid fa-star"></i>
                                </template>
                                <template v-for="n in (5 - Math.floor(ratings.missing_rating))">
                                <i v-if="n === 1 && (ratings.missing_rating - Math.floor(ratings.missing_rating) < 1)" class="fa-solid fa-star-half-stroke"></i>
                                <i v-else class="fa-light fa-star"></i>
                                </template>
                            </div>
                        </div>
                        <div class="small text-end">
                            <p class="text-muted">
                                <span v-if="ratings.missing_rating < 2.5">Frequently</span>
                                <span v-else-if="ratings.missing_rating < 3.5">Sometimes</span>
                                <span v-else-if="ratings.missing_rating < 5">Rarely</span>
                                <span v-else>Never</span>
                                has canceled trades due to missing pins
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PinDetails',
    props: {
        id: {
            type: Number,
            required: true
        },
        name: {
            type: String,
            required: true
        },
        thumb: {
            type: String,
            required: true
        },
        url: {
            type: String,
            required: true
        },
        series: {
            type: String,
            required: false
        },
        seriesUrl: {
            type: String,
            required: false
        },
        edition: {
            type: String,
            required: false
        },
        editionCode: {
            type: String,
            required: false
        },
        editionSize: {
            type: Number,
            required: false
        },
        rarity: {
            type: String,
            required: false
        },
        isGrail: {
            type: Boolean,
            required: false
        },
        isIso: {
            type: Boolean,
            required: false
        },
        isOwned: {
            type: Boolean,
            required: false
        },
        released: {
            type: String,
            required: false
        },
        price: {
            type: String,
            required: false
        },
        profileUrl: {
            type: String,
            required: false
        },
        tags: {
            type: Array,
            required: false
        },
        condition: {
            type: String,
            required: false
        },
        ledger: {
            type: Array|Object,
            required: false
        },
        summary: {
            type: String,
            required: false
        },
        table: {
            type: Object,
            required: false
        },
        ratings: {
            type: Object,
            required: false
        }
    },
    data() {
        return {
            // listing: false
        }
    },
    created() {
        // VueEvent.$on('chosenTradePin', (listing) => {
        //     this.listing = listing;
        // });
    }
}
</script>
